import { AbstractMessageCommand } from 'flux-connection';
import { Injectable, Inject, Optional } from '@angular/core';
import { DataStore } from 'flux-store';
import { CommandInterfaces, Command, StateService } from 'flux-core';
import { Observable, of, from } from 'rxjs';
import { tap, map, switchMap, take } from 'rxjs/operators';
import { ProjectModel } from '../model/project.mdl';
import { ProjectModelStore } from '../../storage/project-model-store';

/**
 * This is the command which changes the role of the project collaborator and send
 * the request to the server.
 *
 * data: {
 *     collabs { id, role} An array of changed collaborator id and role
 * }
 */
@Injectable()
@Command()
export class ChangeProjectCollaborators extends AbstractMessageCommand  {

    public static get dataDefinition(): {}  {
        return {
            collabs: true,
        };
    }

    public static get implements(): Array<CommandInterfaces> {
        return [
            'IMessageCommand',
            'IProjectCommand',
        ];
    }

    constructor(
        protected dataStore: DataStore,
        @Optional() @Inject( StateService ) protected state?: StateService<any, any>,
    ) {
        super()/* istanbul ignore next */;
    }

    public get version(): number {
        return 2;
    }

    public prepareData() {
        this.previousData = {};
        const modelStore = this.dataStore.getModelStore( ProjectModel ) as ProjectModelStore;
        return modelStore.getCollabs( this.resourceId ).pipe(
            tap( collaborators => {
                this.previousData.collabs = collaborators.filter( collab =>
                    this.data.collabs.map( user => user.id ).indexOf( collab.id ) !== -1 );
            }),
        );
    }

    /**
     * execute
     * change collaborator role
     */
    public execute(): Observable<any> {
        const modelStore = this.dataStore.getModelStore( ProjectModel ) as ProjectModelStore;

        return of( this.previousData.collabs ).pipe(
            map( collaborators => {
                if ( !Array.isArray( this.data?.collabs ) || !Array.isArray( collaborators )) {
                    return [];
                }
                return this.data.collabs
                    .map( collab  => {
                        const collabData = collaborators.find( collaborator => collaborator.id === collab.id ) || collab;
                        if ( !collabData ) {
                            return null;
                        }
                        return { ...collabData, role: collab.role };
                    })
                    .filter( collab => !!collab );
            }),
            switchMap( collabs => modelStore.storageMergeCollabs( this.resourceId, collabs ).pipe(
                switchMap(() => this.updateEffectivePermissionRole( modelStore, this.resourceId, collabs )),
            )),
        );
    }

    /**
     * executeResult
     * Update the collaborators with the response
     */
    public executeResult( response: any ): Observable<any> {
        const modelStore = this.dataStore.getModelStore( ProjectModel ) as ProjectModelStore;
        const responseCollabs = Array.isArray( response?.collabs ) ? response.collabs.filter( collab => !!collab ) : [];
        if ( responseCollabs.length > 0 ) {
            return modelStore.storageMergeCollabs( this.resourceId, responseCollabs ).pipe(
                switchMap(() => this.updateEffectivePermissionRole( modelStore, this.resourceId, responseCollabs )),
            );
        }

        if ( Array.isArray( this.data?.collabs ) && this.data.collabs.length > 0 ) {
            return modelStore.getCollabs( this.resourceId ).pipe(
                take( 1 ),
                map( existingCollabs => this.data.collabs
                    .map( collab => {
                        const existing = ( existingCollabs || [] ).find( c => c.id === collab.id );
                        if ( existing ) {
                            return { ...existing, role: collab.role };
                        }
                        return collab;
                    })
                    .filter( collab => !!collab )),
                switchMap( collabs => modelStore.storageMergeCollabs( this.resourceId, collabs ).pipe(
                    switchMap(() => this.updateEffectivePermissionRole( modelStore, this.resourceId, collabs )),
                )),
            );
        }

        return of( true );
    }


    public revert(): Observable<any> {
        const modelStore = this.dataStore.getModelStore( ProjectModel ) as ProjectModelStore ;
        return modelStore.storageMergeCollabs( this.resourceId, this.previousData.collabs );
    }

    protected updateEffectivePermissionRole(
        modelStore: ProjectModelStore,
        resourceId: string,
        collabs: any[],
    ): Observable<any> {
        const currentUserId = this.state?.get( 'CurrentUser' );
        if ( !currentUserId || !resourceId || !Array.isArray( collabs )) {
            return of( true );
        }
        const currentUserCollab = collabs.find( collab => collab?.id === currentUserId && collab?.role !== undefined );
        if ( !currentUserCollab ) {
            return of( true );
        }
        return from( modelStore.updateStatic(
            { id: resourceId },
            { $set: { effectivePermissionRole: currentUserCollab.role }},
        ));
    }

}

Object.defineProperty( ChangeProjectCollaborators, 'name', {
    value: 'ChangeProjectCollaborators',
});
